import * as THREE from "three";
import { acceleratedRaycast, MeshBVH } from "three-mesh-bvh";
import { CONFIG, columnX, columnZ } from "./config.js";
import { MIN_Y, WORLD_SIZE } from "./terrain/constants.js";

const COLLISION_RADIUS = 48;
const REBUILD_DISTANCE = 24;

export default class TerrainCollider {
    constructor(terrain) {
        this.terrain = terrain;
        this.focusI = Math.floor(WORLD_SIZE / 2);
        this.focusJ = Math.floor(WORLD_SIZE / 2);
        this.collisionMesh = this.buildCollisionMesh(new THREE.BufferGeometry());
    }

    needsRebuild(i, j) {
        return (
            Math.abs(i - this.focusI) > REBUILD_DISTANCE ||
            Math.abs(j - this.focusJ) > REBUILD_DISTANCE
        );
    }

    heightAt(i, j) {
        return this.terrain.heightAt(i, j);
    }

    addQuad(positions, vertices) {
        const order = [0, 1, 2, 0, 2, 3];
        for (const index of order) {
            positions.push(...vertices[index]);
        }
    }

    addPatchWalls(positions, minI, maxI, minJ, maxJ) {
        const minX = columnX(minI) - 0.5;
        const maxX = columnX(maxI) + 0.5;
        const minZ = columnZ(minJ) - 0.5;
        const maxZ = columnZ(maxJ) + 0.5;
        const bottomY = MIN_Y - 0.5;
        const topY = bottomY + CONFIG.TERRAIN_BOUNDARY_HEIGHT + 80;

        const walls = [
            [
                [minX, bottomY, minZ],
                [minX, topY, minZ],
                [minX, topY, maxZ],
                [minX, bottomY, maxZ],
            ],
            [
                [maxX, bottomY, maxZ],
                [maxX, topY, maxZ],
                [maxX, topY, minZ],
                [maxX, bottomY, minZ],
            ],
            [
                [minX, bottomY, minZ],
                [maxX, bottomY, minZ],
                [maxX, topY, minZ],
                [minX, topY, minZ],
            ],
            [
                [maxX, bottomY, maxZ],
                [minX, bottomY, maxZ],
                [minX, topY, maxZ],
                [maxX, topY, maxZ],
            ],
        ];

        for (const wall of walls) {
            this.addQuad(positions, wall);
        }
    }

    buildGeometry(centerI, centerJ) {
        const positions = [];
        const minI = Math.max(0, centerI - COLLISION_RADIUS);
        const maxI = Math.min(WORLD_SIZE - 1, centerI + COLLISION_RADIUS);
        const minJ = Math.max(0, centerJ - COLLISION_RADIUS);
        const maxJ = Math.min(WORLD_SIZE - 1, centerJ + COLLISION_RADIUS);

        for (let j = minJ; j <= maxJ; j++) {
            for (let i = minI; i <= maxI; i++) {
                const h = this.heightAt(i, j);
                if (h < MIN_Y) continue;

                const x0 = columnX(i) - 0.5;
                const x1 = columnX(i) + 0.5;
                const z0 = columnZ(j) - 0.5;
                const z1 = columnZ(j) + 0.5;
                const top = h + 0.5;

                this.addQuad(positions, [
                    [x0, top, z1],
                    [x1, top, z1],
                    [x1, top, z0],
                    [x0, top, z0],
                ]);

                const eastEdge = i === maxI;
                const westEdge = i === minI;
                const northEdge = j === maxJ;
                const southEdge = j === minJ;

                const east = eastEdge ? MIN_Y - 1 : this.heightAt(i + 1, j);
                const west = westEdge ? MIN_Y - 1 : this.heightAt(i - 1, j);
                const north = northEdge ? MIN_Y - 1 : this.heightAt(i, j + 1);
                const south = southEdge ? MIN_Y - 1 : this.heightAt(i, j - 1);

                if (east < h) {
                    const bottom = east + 0.5;
                    this.addQuad(positions, [
                        [x1, bottom, z0],
                        [x1, top, z0],
                        [x1, top, z1],
                        [x1, bottom, z1],
                    ]);
                }
                if (west < h) {
                    const bottom = west + 0.5;
                    this.addQuad(positions, [
                        [x0, bottom, z1],
                        [x0, top, z1],
                        [x0, top, z0],
                        [x0, bottom, z0],
                    ]);
                }
                if (north < h) {
                    const bottom = north + 0.5;
                    this.addQuad(positions, [
                        [x1, bottom, z1],
                        [x1, top, z1],
                        [x0, top, z1],
                        [x0, bottom, z1],
                    ]);
                }
                if (south < h) {
                    const bottom = south + 0.5;
                    this.addQuad(positions, [
                        [x0, bottom, z0],
                        [x0, top, z0],
                        [x1, top, z0],
                        [x1, bottom, z0],
                    ]);
                }
            }
        }

        this.addPatchWalls(positions, minI, maxI, minJ, maxJ);

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(positions, 3),
        );
        geometry.computeBoundingSphere();
        geometry.boundsTree = new MeshBVH(geometry, { targetLeafSize: 12 });
        return geometry;
    }

    buildCollisionMesh(geometry) {
        const material = new THREE.MeshBasicMaterial({
            color: 0xffc857,
            wireframe: true,
            transparent: true,
            opacity: 0.35,
            side: THREE.DoubleSide,
            toneMapped: false,
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = "terrain-collider";
        mesh.raycast = acceleratedRaycast;
        mesh.layers.enable(1);
        return mesh;
    }

    rebuild(centerI, centerJ) {
        const spawn = this.terrain.layout?.spawn;
        if (centerI == null) {
            centerI = spawn ? spawn.x | 0 : this.focusI;
        }
        if (centerJ == null) {
            centerJ = spawn ? spawn.z | 0 : this.focusJ;
        }

        this.focusI = centerI;
        this.focusJ = centerJ;

        const previous = this.collisionMesh;
        const geometry = this.buildGeometry(centerI, centerJ);
        this.collisionMesh = this.buildCollisionMesh(geometry);

        if (previous?.geometry) {
            previous.geometry.dispose();
            previous.material?.dispose?.();
        }

        return this.collisionMesh;
    }
}
